import { Injectable } from '@angular/core';

type GtagFn = (...args: unknown[]) => void;

/**
 * Injects the Google Analytics 4 (gtag.js) tracking snippet at runtime and exposes
 * thin helpers for sending page views, custom events and user identity.
 *
 * The measurement id is supplied by the caller (from the active environment), so this
 * service stays decoupled from the app's environment files. When the id is empty — as
 * it is for every environment except production — initialisation is a no-op, which is
 * how GA4 is kept off outside PROD. This mirrors {@link ClarityService}.
 *
 * Automatic page_view is disabled (`send_page_view: false`) because the app uses hash
 * routing (`useHash: true`), which gtag's automatic page_view does not capture
 * reliably — page views are sent manually via {@link trackPageView} on NavigationEnd.
 *
 * Based on the official snippet from https://developers.google.com/analytics.
 */
@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private initialized = false;

  init(measurementId: string | null | undefined): void {
    if (this.initialized || !measurementId) {
      return;
    }

    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    this.initialized = true;

    const win = window as unknown as { dataLayer?: unknown[]; gtag?: GtagFn };
    win.dataLayer = win.dataLayer || [];
    if (!win.gtag) {
      win.gtag = function (...args: unknown[]) {
        (win.dataLayer as unknown[]).push(args);
      };
    }

    const gtag = win.gtag;
    gtag('js', new Date());
    gtag('config', measurementId, { send_page_view: false });

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://www.googletagmanager.com/gtag/js?id=' + measurementId;

    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      document.head.appendChild(script);
    }
  }

  /** Sends a manual page_view. `path` should already be query/hash-stripped. */
  trackPageView(path: string): void {
    this.trackEvent('page_view', {
      page_path: path,
      page_location: typeof window !== 'undefined' ? window.location.href : path,
      page_title: typeof document !== 'undefined' ? document.title : undefined,
    });
  }

  /** Sends a custom GA4 event. No-op until {@link init} has loaded gtag. */
  trackEvent(name: string, params: Record<string, unknown> = {}): void {
    if (!this.initialized) {
      return;
    }
    const gtag = (window as unknown as { gtag?: GtagFn }).gtag;
    if (!gtag) {
      return;
    }
    gtag('event', name, params);
  }

  /**
   * Binds the logged-in user's stable, non-PII surrogate ids to GA4:
   * `user_id` (for cross-session/device stitching) and an `org_id` user-property.
   * Empty ids are skipped.
   */
  setUser(userId: string | null | undefined, orgId: string | null | undefined): void {
    if (!this.initialized) {
      return;
    }
    const gtag = (window as unknown as { gtag?: GtagFn }).gtag;
    if (!gtag) {
      return;
    }
    if (userId) {
      gtag('set', { user_id: userId });
    }
    if (orgId) {
      gtag('set', 'user_properties', { org_id: orgId });
    }
  }
}
